import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Pencil, X, Check, AlertTriangle } from 'lucide-react'
import {
  setInvoiceCurrencyRateOverride,
  clearInvoiceCurrencyRateOverride,
  createManualCurrencyRate,
} from '@/services/currencyRateService'
import { fetchAffectedInvoicesCount } from '@/services/currencyDefinitionService'
import { showSuccessToast, showErrorToast } from '@/hooks/use-toast'
import type { CurrencyRateInfo, Invoice } from '../types'

interface CurrencyRateInputProps {
  invoice: Invoice
  currencyRateInfo: CurrencyRateInfo | null
  onInvoiceRefresh: () => Promise<void>
  currencyCode: string
  readOnly?: boolean
}

export const CurrencyRateInput = ({
  invoice,
  currencyRateInfo,
  onInvoiceRefresh,
  currencyCode,
  readOnly,
}: CurrencyRateInputProps): JSX.Element | null => {
  const [isEditing, setIsEditing] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [affectedCount, setAffectedCount] = useState<number | null>(null)
  const [isOverrideOnly, setIsOverrideOnly] = useState(true)

  const isHidden = currencyCode === 'EUR' || !currencyRateInfo

  useEffect(() => {
    if (isHidden || !isEditing || !currencyRateInfo) return

    const fetchCount = async () => {
      try {
        const count = await fetchAffectedInvoicesCount(
          currencyCode,
          currencyRateInfo.rate_date,
          invoice.id
        )
        setAffectedCount(count)
      } catch (error) {
        console.error('Failed to fetch affected invoices count:', error)
      }
    }
    void fetchCount()
  }, [isHidden, isEditing, currencyCode, currencyRateInfo, invoice.id])

  if (isHidden) {
    return null
  }

  const handleEdit = () => {
    setInputValue(currencyRateInfo?.effective_rate || '')
    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
    setInputValue('')
    setAffectedCount(null)
    setIsOverrideOnly(true)
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      const rateValue = parseFloat(inputValue)

      if (isNaN(rateValue) || rateValue <= 0) {
        showErrorToast('Please enter a valid positive number')
        return
      }

      if (isOverrideOnly) {
        await setInvoiceCurrencyRateOverride(invoice.id, rateValue)
        showSuccessToast('Currency rate override set for this invoice')
      } else {
        await createManualCurrencyRate({
          currency_code: currencyCode,
          rate_date: currencyRateInfo!.rate_date,
          rate_to_eur: rateValue,
        })
        showSuccessToast(
          `Shared rate updated for ${currencyCode} (affects ${affectedCount || 0} invoices)`
        )
      }

      await onInvoiceRefresh()
      setIsEditing(false)
      setInputValue('')
      setAffectedCount(null)
      setIsOverrideOnly(true)
    } catch (error) {
      console.error('Failed to update currency rate:', error)
      showErrorToast('Failed to update currency rate')
    } finally {
      setIsSaving(false)
    }
  }

  const handleClearOverride = async () => {
    try {
      setIsSaving(true)
      await clearInvoiceCurrencyRateOverride(invoice.id)
      showSuccessToast('Currency rate override cleared')

      await onInvoiceRefresh()
    } catch (error) {
      console.error('Failed to clear currency rate override:', error)
      showErrorToast('Failed to clear currency rate override')
    } finally {
      setIsSaving(false)
    }
  }

  const providerBadge = (() => {
    const provider = currencyRateInfo.rate_provider
    if (provider === 'bnb') {
      return (
        <Badge className="bg-green-100 text-green-800 hover:bg-green-100 text-xs">
          BNB
        </Badge>
      )
    }
    if (provider === 'ecb') {
      return (
        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-xs">
          ECB
        </Badge>
      )
    }
    if (provider === 'openexchangerates') {
      return (
        <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100 text-xs">
          Open Exchange
        </Badge>
      )
    }
    if (provider === 'manual') {
      return (
        <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 text-xs">
          Manual
        </Badge>
      )
    }
    return null
  })()

  if (!isEditing) {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">
            {parseFloat(currencyRateInfo.effective_rate).toString()}
          </span>
          {currencyRateInfo.is_overridden ? (
            <Badge variant="default" className="text-xs">
              Override
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-xs">
              Shared
            </Badge>
          )}
          {providerBadge}
          {!readOnly && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleEdit}
              className="h-6 px-2"
            >
              <Pencil className="h-3 w-3" />
            </Button>
          )}
        </div>
        {currencyRateInfo.is_overridden &&
          currencyRateInfo.shared_rate && (
            <p className="text-xs text-neutral-500">
              Shared rate: {parseFloat(currencyRateInfo.shared_rate).toString()}
            </p>
          )}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input
          type="text"
          inputMode="decimal"
          value={inputValue}
          onChange={(e) => {
            const value = e.target.value.replace(/,/g, '.').replace(/[^\d.]/g, '')
            const parts = value.split('.')
            if (parts.length > 2) return
            if (parts.length === 2 && parts[1].length > 8) return
            setInputValue(value)
          }}
          className="h-9 flex-1"
          placeholder="Enter rate (e.g., 1.854601)"
          autoFocus
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSave}
          disabled={isSaving}
          className="h-9 px-3"
        >
          <Check className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          disabled={isSaving}
          className="h-9 px-3"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {affectedCount !== null && affectedCount > 0 && (
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isOverrideOnly}
              onChange={(e) => setIsOverrideOnly(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span>Override for this invoice only</span>
          </label>

          <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div>
              {isOverrideOnly ? (
                <>
                  <p className="font-medium">
                    This will create an override for this invoice only.
                  </p>
                  <p className="text-amber-700">
                    {affectedCount} other invoice{affectedCount !== 1 ? 's' : ''}{' '}
                    will keep using the shared rate.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-medium">
                    This will update the shared rate for all invoices.
                  </p>
                  <p className="text-amber-700">
                    This will affect {affectedCount} invoice
                    {affectedCount !== 1 ? 's' : ''} using this rate.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {currencyRateInfo.is_overridden && (
        <div className="flex items-center gap-2">
          <p className="text-xs text-neutral-600">
            Currently using override.
          </p>
          {!readOnly && (
            <Button
              variant="link"
              size="sm"
              onClick={handleClearOverride}
              disabled={isSaving}
              className="h-auto p-0 text-xs"
            >
              Use Shared Rate
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
